import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { PlannedQuestion, QuestionGroup, QuickReply } from '@entities/search-session';

import { containsLabel, quickReplyText, toggleInDraft } from './quickReply';
import type { ChatDraft } from './useChatDraft';

export interface QuickReplyDraft {
  /** Подсказки, чьи подписи сейчас стоят в черновике: они же уйдут кодами. */
  active: string[];
  toggle: (group: QuestionGroup, reply: QuickReply) => void;
}

/**
 * Подсказки текущего вопроса, вписанные в черновик (FE-595).
 *
 * Коды уходят вместе с сообщением, чтобы backend применил нажатое
 * детерминированно, — но только те, чья подпись **всё ещё в тексте**. Стёр
 * человек подпись руками — кода нет, и сообщение разбирается как обычный
 * текст. Набранное руками кодом не становится никогда: «kein SUV» с кнопкой
 * «SUV» совпадает буквами, а не смыслом.
 *
 * Выбор живёт до следующего вопроса: новая реплика Karl — новые подсказки, и
 * прошлые нажатия к ним не относятся.
 */
export function useQuickReplyDraft(
  question: PlannedQuestion | null,
  draft: ChatDraft,
): QuickReplyDraft {
  const { t, i18n } = useTranslation();
  const key = question === null ? null : `${question.question_code}:${question.repeat_index}`;
  const [picked, setPicked] = useState<{ key: string | null; codes: string[] }>({
    key,
    codes: [],
  });

  const labels = useMemo(() => {
    const hasKey = (labelKey: string) => i18n.exists(labelKey);
    return new Map(
      (question?.groups ?? []).flatMap((group) =>
        group.quick_replies.map((reply) => [reply.code, quickReplyText(reply, t, hasKey)] as const),
      ),
    );
  }, [question, t, i18n]);

  const codes = picked.key === key ? picked.codes : [];
  const active = codes.filter((code) => {
    const label = labels.get(code);
    return label !== undefined && containsLabel(draft.value, label);
  });

  const toggle = (group: QuestionGroup, reply: QuickReply) => {
    const next = toggleInDraft(draft.value, active, group, reply, (code) => labels.get(code));
    setPicked({ key, codes: next.codes });
    draft.set(next.text);
  };

  return { active, toggle };
}
